import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { rateInput } from '../lib/numberInput'
import { formatFtwe } from '../lib/rates'
import {
  FTWE_LABEL,
  FTWE_TIERS,
  FTWE_TIER_LABELS,
  FTWE_TIER_ORDER,
  type FtweTier,
  type RateCardSnapshot,
} from '../types'

const FTWE_HINT =
  "Minutes of agent work that count as one person for one day. Bigger number, fewer FTWEs. Changes the estimate on the home page and the billing page for every month shown. Kept as is, the latest card's tier carries over, which may be one already scheduled."

// The option that goes back to leaving the card's FTWE setting alone. A
// Select item cannot carry an empty value, so this stands for ''.
const KEEP = 'keep'

/** A tier as the select offers it: a named tier with the minutes it stands
 *  for, or Custom, whose minutes are typed. */
function ftweOptionLabel(tier: FtweTier): string {
  return tier === 'custom'
    ? FTWE_TIER_LABELS.custom
    : formatFtwe(tier, FTWE_TIERS[tier])
}

interface FtweTierFieldProps {
  /** The tier to move to, or '' to keep the card's setting. */
  tier: FtweTier | ''
  /** The typed minutes of a Custom tier. */
  multiplier: string
  /** Why the typed Custom minutes cannot be previewed, or null. */
  problem: string | null
  disabled: boolean
  /** The org's card in force today, shown as the select's hint. */
  rateCard: RateCardSnapshot | null
  onTierChange: (tier: FtweTier | '') => void
  onMultiplierChange: (multiplier: string) => void
}

/** The FTWE tier in the rate card change dialog: keep the card's setting,
 *  pick a named tier, or type a Custom number of minutes. */
export function FtweTierField({
  tier,
  multiplier,
  problem,
  disabled,
  rateCard,
  onTierChange,
  onMultiplierChange,
}: FtweTierFieldProps) {
  return (
    <div className="grid gap-2">
      <Label htmlFor="ftwe_tier">{FTWE_LABEL}</Label>
      <Select
        value={tier}
        disabled={disabled}
        onValueChange={(value) =>
          onTierChange(value === KEEP ? '' : (value as FtweTier))
        }>
        <SelectTrigger id="ftwe_tier" aria-describedby="ftwe_tier-hint">
          <SelectValue
            placeholder={`Today: ${
              rateCard?.ftwe_tier && rateCard.ftwe_multiplier
                ? formatFtwe(rateCard.ftwe_tier, rateCard.ftwe_multiplier)
                : '—'
            }`}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP}>Keep as is</SelectItem>
          {FTWE_TIER_ORDER.map((option) => (
            <SelectItem key={option} value={option}>
              {ftweOptionLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="ftwe_tier-hint" className="text-xs text-muted-foreground">
        {FTWE_HINT}
      </p>
      {tier === 'custom' && (
        <>
          <Input
            id="ftwe_multiplier"
            inputMode="decimal"
            disabled={disabled}
            placeholder="Minutes per person-day"
            aria-label="Custom FTWE multiplier"
            value={multiplier}
            aria-invalid={problem !== null}
            onChange={(e) => {
              const value = rateInput(e.target.value)
              if (value === null) return
              onMultiplierChange(value)
            }}
          />
          {problem && (
            <p className="text-xs text-error-low-contrast">{problem}</p>
          )}
        </>
      )}
    </div>
  )
}
